#!/usr/bin/env bun
import { Command } from 'commander';
import inquirer from 'inquirer';
import {
  generateTypes,
  configGet,
  configSet,
  configInit,
  login,
  logout,
  listRows,
  createRow,
  updateRow,
  listTables,
  getTable,
  createTable,
  updateTable,
  deleteTable
} from './index';
import { ConfigManager } from './config/manager';
import { error } from './utils/output';
import { getConfig, getConfigValue } from './utils/config';
import type { OutputFormat } from './utils/output';
import type { ResolvedConfig } from './config/types';
import { AuthError } from './utils/client';

(async () => {
  const pkg = await import('../../package.json');
  const program = new Command();

  program
    .name('baserow')
    .description('CLI for interacting with Baserow')
    .version(pkg.default.version);

  function handleCommandError(err: unknown, commandName: string): never {
    if (err instanceof AuthError) {
      error(err.message);
    } else if (err instanceof Error) {
      error(`${commandName} failed: ${err.message}`);
    } else {
      error(`${commandName} failed: An unknown error occurred`);
    }
    process.exit(1);
  }

  program
    .command('login')
    .description('Log in to Baserow to obtain a JWT token for authentication')
    .option('-l, --local', 'Store credentials in local project config (less secure)')
    .option('-u, --url <url>', 'Baserow instance URL')
    .action(async (options) => {
      try {
        const answers = await inquirer.prompt([
          {
            type: 'input',
            name: 'email',
            message: 'Email:'
          },
          {
            type: 'password',
            name: 'password',
            message: 'Password:'
          }
        ]);
        await login(options, { email: answers.email, password: answers.password });
      } catch (err: unknown) {
        handleCommandError(err, 'Login');
      }
    });

  program
    .command('logout')
    .description('Log out from Baserow and clear stored credentials')
    .option('-l, --local', 'Remove credentials from local project config instead of global')
    .action(async (options) => {
      try {
        await logout(options);
      } catch (err: unknown) {
        handleCommandError(err, 'Logout');
      }
    });

  program
    .command('config')
    .description('Get or set configuration values')
    .argument('[key]', 'Configuration key (e.g., "url" or "token")')
    .argument('[value]', 'Value to set')
    .option('-g, --global', 'Use global configuration')
    .option('-f, --format <format>', 'Output format (table, json, nushell)', 'table')
    .action(async (key?: string, value?: string, options?: { global?: boolean, format?: OutputFormat }) => {
      try {
        console.log('Config command:', key, value, options);
        if (value) {
          if (!key) {
            throw new Error('A key is required when setting a value');
          }
          await configSet(key, value, options || {});
        } else {
          await configGet(key, options);
        }
      } catch (err: unknown) {
        handleCommandError(err, value ? 'Config set' : 'Config get');
      }
    });

  program
    .command('init')
    .description('Initialize a new project configuration')
    .argument('[name]', 'Project name')
    .action(async (name?: string) => {
      try {
        await configInit(name);
      } catch (err: unknown) {
        handleCommandError(err, 'Init');
      }
    });

  program
    .command('generate-types')
    .description('Generate TypeScript types from Baserow database')
    .option('-u, --url <url>', 'Baserow instance URL (can be set in config)')
    .option('-t, --token <token>', 'API token (can be set in config)')
    .option('-d, --database <id>', 'Database ID (can be set in config)')
    .option('-o, --output <path>', 'Output directory (defaults to config or "./types")')
    .option('--tables <tables>', 'Comma-separated list of table names to generate types for')
    .option('--token-type <type>', 'Token type ("JWT" or "Token", defaults to "Token")')
    .action(async (options) => {
      try {
        if (options.tables) {
          options.tables = options.tables.split(',').map(t => t.trim());
        }
        const configManager = ConfigManager.getInstance();
        const resolvedConfig = configManager.resolveConfig(options);
        if (!resolvedConfig.url) resolvedConfig.url = await getConfigValue('url') as string;
        if (!resolvedConfig.token) resolvedConfig.token = await getConfigValue('token') as string;
        if (!resolvedConfig.database) resolvedConfig.database = await getConfigValue('databaseId') as string;
        if (!resolvedConfig.output && !options.output) {
          const configOutput = await getConfigValue('outputPath');
          if (configOutput) resolvedConfig.output = configOutput;
        }
        if (!resolvedConfig.tables && !options.tables) {
          const configTables = await getConfigValue('tables');
          if (configTables) resolvedConfig.tables = configTables;
        }
        const missingFields: string[] = [];
        if (!resolvedConfig.url) missingFields.push('url');
        if (!resolvedConfig.token) missingFields.push('token');
        if (!resolvedConfig.database) missingFields.push('database');
        if (missingFields.length > 0) {
          error(`Missing required configuration: ${missingFields.join(', ')}`);
          console.log('\nThese can be set either:');
          console.log('1. In your .baserowrc.jsonc file');
          console.log('2. Via the config command:');
          missingFields.forEach(field => {
            console.log(`   baserow config ${field} <value>`);
          });
          console.log('3. As command line options:');
          missingFields.forEach(field => {
            console.log(`   --${field} <value>`);
          });
          console.log('4. By logging in with "baserow login"');
          process.exit(1);
        }
        const finalConfig: ResolvedConfig = {
          url: resolvedConfig.url!,
          token: resolvedConfig.token!,
          tokenType: resolvedConfig.tokenType || "Token",
          refreshToken: resolvedConfig.refreshToken,
          database: resolvedConfig.database!,
          output: resolvedConfig.output || './types/baserow.ts',
          tables: resolvedConfig.tables
        };
        await generateTypes(finalConfig);
      } catch (err: unknown) {
        handleCommandError(err, 'Type generation');
      }
    });

  const rowsCommand = program
    .command('rows')
    .description('Manage rows in Baserow tables');

  rowsCommand
    .command('list')
    .description('List rows from a table')
    .argument('<table-id>', 'ID of the table to list rows from')
    .option('-l, --limit <limit>', 'Maximum number of rows to return', '100')
    .option('-o, --offset <offset>', 'Offset for pagination', '0')
    .option('-s, --search <term>', 'Search term to filter rows')
    .option('-f, --filter <filters...>', 'Filters in format field_id:op:value (e.g. "field_1:equal:value")')
    .option('--field-names', 'Use field names instead of field IDs', false)
    .option('--format <format>', 'Output format (table, json, nushell)', 'table')
    .action(async (tableId, options) => {
      try {
        await listRows(parseInt(tableId), options);
      } catch (err: unknown) {
        handleCommandError(err, 'List rows');
      }
    });

  rowsCommand
    .command('create')
    .description('Create a new row in a table')
    .argument('<table-id>', 'ID of the table to create a row in')
    .option('-d, --data <data>', 'JSON string with row data')
    .option('-f, --field <fieldData...>', 'Field data in format field_id:value')
    .option('--field-names', 'Use field names instead of field IDs', false)
    .option('--format <format>', 'Output format (table, json, nushell)', 'table')
    .action(async (tableId, options) => {
      try {
        await createRow(parseInt(tableId), options);
      } catch (err: unknown) {
        handleCommandError(err, 'Create row');
      }
    });

  // ... Add other commands as needed ...

  program.parse(process.argv);
})(); 